/**
 * DealerMind Floorplan Optimization Engine
 * All calculation logic based on the specification.
 */

export interface FloorplanInputs {
  totalInterestPaid: number;
  totalFeesPaid: number;
  avgBalance: number;
  unitsSold: number;
  avgDaysInStock: number;
  periodDays: number;
  curtailmentPaidPrincipal: number;
  unusedLineAmountAvg: number;
  creditLimit: number;
  grossProfitPerUnit: number;
  avgPurchasePrice: number;
  dealerWacc: number;
  inventoryUnitsAvg: number;
  statedRate: number;
  // Fee breakdown
  feeInterest: number;
  feeAdmin: number;
  feeAudit: number;
  feeCurtailment: number;
  feeDoc: number;
  feeTitle: number;
  feeOther: number;
  feeAncillary1: number;
  feeAncillary2: number;
  feeAncillary3: number;
  feeAncillary4: number;
}

export interface TrueMonthlyCost {
  value: number;
  flags: string[];
}

export interface EffectiveYield {
  value: number | null;
  pct: number | null;
  flags: string[];
}

export interface PerUnitCost {
  methodA: number | null;
  methodB: number | null;
  preferred: number | null;
  flags: string[];
}

export interface TurnSensitivity {
  turnsCurrent: number;
  turnsTarget: number;
  daysCurrent: number;
  daysTarget: number;
  costPerUnitCurrent: number;
  costPerUnitTarget: number;
  annualSavings: number;
  plusOneTurnSavings: number;
}

export interface HiddenCostBreakdown {
  totalCost: number;
  interestPct: number;
  feePctTotal: number;
  feeAdminPct: number;
  feeAuditPct: number;
  feeCurtailmentPct: number;
  feeDocPct: number;
  feeTitlePct: number;
  feeOtherPct: number;
  unusedLinePct: number;
  unusedLineCost: number;
  chartSlices: { name: string; value: number; color: string }[];
}

export interface CurtailmentDrag {
  totalCurtailmentCapital: number;
  curtailmentDrag: number;
  effectiveYieldNoCurtail: number;
  effectiveYield10Curtail: number;
  simCurtailmentCapital: number;
}

export interface CapitalDrag {
  inventoryRoiAnnual: number;
  floorplanYield: number;
  grossPerDayPerUnit: number;
  flag: "destroying_equity" | "ok" | "insufficient_data";
}

export interface LenderCompareRow {
  lenderId: string;
  lenderName: string;
  statedRate: number | null;
  effectiveYield: number;
  totalAnnualCost: number;
  costPerUnit: number | null;
  hiddenFeesPct: number;
  rank: number;
  colorCode: "green" | "yellow" | "red";
}

export interface BlendedMetrics {
  blendedEffectiveYield: number;
  blendedAnnualCost: number;
  blendedCostPerUnit: number | null;
  blendedHiddenFeesPct: number;
  totalInterestAll: number;
  totalFeesAll: number;
  totalCostAll: number;
  totalUnitsAll: number;
}

export interface ComputedMetrics {
  trueMonthlyCost: TrueMonthlyCost;
  effectiveYield: EffectiveYield;
  perUnitCost: PerUnitCost;
  turnSensitivity: TurnSensitivity | null;
  hiddenCostBreakdown: HiddenCostBreakdown;
  curtailmentDrag: CurtailmentDrag | null;
  capitalDrag: CapitalDrag | null;
  projectedAnnualCost: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function safe(n: number | undefined | null, fallback = 0): number {
  if (n === undefined || n === null || isNaN(n) || !isFinite(n)) return fallback;
  return n;
}

// ─── 1. True Monthly Floorplan Cost ─────────────────────────────────────────

export function computeTrueMonthlyCost(inp: FloorplanInputs): TrueMonthlyCost {
  const value = safe(inp.totalInterestPaid) + safe(inp.totalFeesPaid);
  const flags: string[] = [];
  if (inp.totalInterestPaid <= 0 && inp.totalFeesPaid <= 0) flags.push("no_cost_data");
  return { value, flags };
}

// ─── 2. Effective Annualized Yield ──────────────────────────────────────────

export function computeEffectiveYield(inp: FloorplanInputs): EffectiveYield {
  const flags: string[] = [];
  const { value: trueCost } = computeTrueMonthlyCost(inp);
  const balance = safe(inp.avgBalance);
  const periodDays = safe(inp.periodDays, 30);

  if (balance <= 0) {
    flags.push("missing_avg_balance");
    return { value: null, pct: null, flags };
  }

  const annualizedFactor = 365 / periodDays;
  const yieldDecimal = (trueCost / balance) * annualizedFactor;
  const yieldPct = yieldDecimal * 100;

  return { value: yieldDecimal, pct: yieldPct, flags };
}

// ─── 3. Per-Unit Floorplan Cost ─────────────────────────────────────────────

export function computePerUnitCost(inp: FloorplanInputs): PerUnitCost {
  const flags: string[] = [];
  const { value: trueCost } = computeTrueMonthlyCost(inp);
  const unitsSold = safe(inp.unitsSold);
  const periodDays = safe(inp.periodDays, 30);
  const inventoryUnitsAvg = safe(inp.inventoryUnitsAvg);
  const avgDays = safe(inp.avgDaysInStock);

  // Method A: simple
  let methodA: number | null = null;
  if (unitsSold > 0) {
    methodA = trueCost / unitsSold;
  } else {
    flags.push("missing_units_sold");
  }

  // Method B: turn-based
  let methodB: number | null = null;
  if (periodDays > 0 && inventoryUnitsAvg > 0 && avgDays > 0) {
    const dailyCostTotal = trueCost / periodDays;
    const dailyCostPerUnit = dailyCostTotal / inventoryUnitsAvg;
    methodB = dailyCostPerUnit * avgDays;
  }

  const preferred = methodB ?? methodA;
  return { methodA, methodB, preferred, flags };
}

// ─── 4. Turn Sensitivity Model ──────────────────────────────────────────────

export function computeTurnSensitivity(
  inp: FloorplanInputs,
  turnsTargetOverride?: number,
): TurnSensitivity | null {
  const avgDays = safe(inp.avgDaysInStock);
  if (avgDays <= 0) return null;

  const { preferred: perUnit } = computePerUnitCost(inp);
  if (perUnit === null) return null;

  const turnsCurrent = 365 / avgDays;
  const turnsTarget = turnsTargetOverride ?? turnsCurrent + 1;

  const daysCurrent = avgDays;
  const daysTarget = 365 / turnsTarget;

  const costPerUnitCurrent = perUnit;
  const costPerUnitTarget = costPerUnitCurrent * (daysTarget / daysCurrent);

  const annualUnits = safe(inp.unitsSold) * 12;
  const annualSavings = (costPerUnitCurrent - costPerUnitTarget) * annualUnits;

  const plusOneTurnsTarget = turnsCurrent + 1;
  const plusOneDaysTarget = 365 / plusOneTurnsTarget;
  const plusOneCostTarget = costPerUnitCurrent * (plusOneDaysTarget / daysCurrent);
  const plusOneTurnSavings = (costPerUnitCurrent - plusOneCostTarget) * annualUnits;

  return {
    turnsCurrent,
    turnsTarget,
    daysCurrent,
    daysTarget,
    costPerUnitCurrent,
    costPerUnitTarget,
    annualSavings,
    plusOneTurnSavings,
  };
}

// ─── 5. Hidden Cost Detector ─────────────────────────────────────────────────

const SLICE_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "#a78bfa",
  "#f472b6",
  "#34d399",
];

export function computeHiddenCostBreakdown(inp: FloorplanInputs): HiddenCostBreakdown {
  const unusedLineCost =
    inp.unusedLineAmountAvg > 0 && inp.dealerWacc > 0
      ? inp.unusedLineAmountAvg * (inp.dealerWacc / 12)
      : 0;

  const totalCost = safe(inp.totalInterestPaid) + safe(inp.totalFeesPaid) + unusedLineCost;

  if (totalCost <= 0) {
    return {
      totalCost: 0,
      interestPct: 0,
      feePctTotal: 0,
      feeAdminPct: 0,
      feeAuditPct: 0,
      feeCurtailmentPct: 0,
      feeDocPct: 0,
      feeTitlePct: 0,
      feeOtherPct: 0,
      unusedLinePct: 0,
      unusedLineCost: 0,
      chartSlices: [],
    };
  }

  const pct = (n: number) => (n / totalCost) * 100;

  const interestPct = pct(safe(inp.totalInterestPaid));
  const feeAdminPct = pct(safe(inp.feeAdmin));
  const feeAuditPct = pct(safe(inp.feeAudit));
  const feeCurtailmentPct = pct(safe(inp.feeCurtailment));
  const feeDocPct = pct(safe(inp.feeDoc));
  const feeTitlePct = pct(safe(inp.feeTitle));
  const unusedLinePct = pct(unusedLineCost);

  const accountedFees =
    safe(inp.feeAdmin) +
    safe(inp.feeAudit) +
    safe(inp.feeCurtailment) +
    safe(inp.feeDoc) +
    safe(inp.feeTitle) +
    safe(inp.feeAncillary1) +
    safe(inp.feeAncillary2) +
    safe(inp.feeAncillary3) +
    safe(inp.feeAncillary4);
  const feeOther = Math.max(0, safe(inp.totalFeesPaid) - accountedFees);
  const feeOtherPct = pct(feeOther);
  const feePctTotal = pct(safe(inp.totalFeesPaid));

  const rawSlices = [
    { name: "Interest", value: safe(inp.totalInterestPaid) },
    { name: "Admin/Floor Fee", value: safe(inp.feeAdmin) },
    { name: "Audit Fee", value: safe(inp.feeAudit) },
    { name: "Curtailment Fee", value: safe(inp.feeCurtailment) },
    { name: "Doc Fee", value: safe(inp.feeDoc) },
    { name: "Title Fee", value: safe(inp.feeTitle) },
    { name: "Other Fees", value: feeOther },
    { name: "Unused Line Cost", value: unusedLineCost },
  ].filter((s) => s.value > 0);

  const chartSlices = rawSlices.map((s, i) => ({
    ...s,
    value: parseFloat(((s.value / totalCost) * 100).toFixed(2)),
    color: SLICE_COLORS[i % SLICE_COLORS.length],
  }));

  return {
    totalCost,
    interestPct,
    feePctTotal,
    feeAdminPct,
    feeAuditPct,
    feeCurtailmentPct,
    feeDocPct,
    feeTitlePct,
    feeOtherPct,
    unusedLinePct,
    unusedLineCost,
    chartSlices,
  };
}

// ─── 6. Capital Drag / Equity Destruction ────────────────────────────────────

export function computeCapitalDrag(inp: FloorplanInputs): CapitalDrag | null {
  const grossPerUnit = safe(inp.grossProfitPerUnit);
  const avgDays = safe(inp.avgDaysInStock);
  const avgPurchasePrice = safe(inp.avgPurchasePrice);

  if (grossPerUnit <= 0 || avgDays <= 0 || avgPurchasePrice <= 0) return null;

  const { pct: floorplanYield } = computeEffectiveYield(inp);
  if (floorplanYield === null) return null;

  const grossPerDayPerUnit = grossPerUnit / avgDays;
  const inventoryRoiAnnual = (grossPerUnit / avgPurchasePrice) * (365 / avgDays) * 100;

  const flag: CapitalDrag["flag"] =
    floorplanYield > inventoryRoiAnnual ? "destroying_equity" : "ok";

  return { inventoryRoiAnnual, floorplanYield, grossPerDayPerUnit, flag };
}

// ─── 7. Curtailment Drag ─────────────────────────────────────────────────────

export function computeCurtailmentDrag(inp: FloorplanInputs): CurtailmentDrag | null {
  const balance = safe(inp.avgBalance);
  if (balance <= 0) return null;

  const { value: trueCost } = computeTrueMonthlyCost(inp);
  const avgDays = safe(inp.avgDaysInStock, 30);
  const curtailmentCapital = safe(inp.curtailmentPaidPrincipal);

  const curtailmentDrag = avgDays > 0 ? curtailmentCapital / avgDays : 0;

  const effectiveYieldNoCurtail = (trueCost / balance) * 12 * 100;
  const simCurtailmentCapital = balance * 0.1;
  const reducedBalance = balance - simCurtailmentCapital;
  const effectiveYield10Curtail =
    reducedBalance > 0 ? (trueCost / reducedBalance) * 12 * 100 : 0;

  return {
    totalCurtailmentCapital: curtailmentCapital,
    curtailmentDrag,
    effectiveYieldNoCurtail,
    effectiveYield10Curtail,
    simCurtailmentCapital,
  };
}

// ─── 8. Lender Comparison Table ──────────────────────────────────────────────

export function computeComparisonTable(
  lenders: Array<{ id: string; name: string }>,
  entries: Array<any>,
  assumedDays: number,
  assumedBalance: number,
): LenderCompareRow[] {
  const rows = lenders
    .map((lender) => {
      const lenderEntries = entries.filter((e) => e.lenderId === lender.id);
      const latest = lenderEntries[0];
      if (!latest) return null;

      const inp = entryToInputs(latest, { avgDaysInStock: assumedDays, avgBalance: assumedBalance });

      const { pct: yield_ } = computeEffectiveYield(inp);
      const { value: monthly } = computeTrueMonthlyCost(inp);
      const { preferred: perUnit } = computePerUnitCost(inp);

      const totalFees = safe(inp.totalFeesPaid);
      const totalInterest = safe(inp.totalInterestPaid);
      const totalCost = totalFees + totalInterest;
      const hiddenFeesPct = totalCost > 0 ? (totalFees / totalCost) * 100 : 0;

      return {
        lenderId: lender.id,
        lenderName: lender.name,
        statedRate: inp.statedRate > 0 ? inp.statedRate : null,
        effectiveYield: yield_ ?? 0,
        totalAnnualCost: monthly * 12,
        costPerUnit: perUnit,
        hiddenFeesPct,
        rank: 0,
        colorCode: "yellow" as const,
      };
    })
    .filter(Boolean) as LenderCompareRow[];

  rows.sort((a, b) => a.effectiveYield - b.effectiveYield);
  rows.forEach((row, i) => {
    row.rank = i + 1;
    if (i === 0) row.colorCode = "green";
    else if (i === rows.length - 1) row.colorCode = "red";
    else row.colorCode = "yellow";
  });

  return rows;
}

// ─── 9. Blended Metrics ──────────────────────────────────────────────────────

export function computeBlendedMetrics(entries: any[]): BlendedMetrics {
  let totalInterestAll = 0;
  let totalFeesAll = 0;
  let totalBalanceAll = 0;
  let totalUnitsAll = 0;

  for (const e of entries) {
    totalInterestAll += parseFloat(e.totalInterestPaid || "0");
    totalFeesAll += parseFloat(e.totalFeesPaid || "0");
    totalBalanceAll += parseFloat(e.avgInventoryBalance || "0");
    totalUnitsAll += parseInt(e.unitsSold || "0");
  }

  const totalCostAll = totalInterestAll + totalFeesAll;
  const blendedEffectiveYield =
    totalBalanceAll > 0 ? (totalCostAll / totalBalanceAll) * 12 * 100 : 0;
  const blendedCostPerUnit = totalUnitsAll > 0 ? totalCostAll / totalUnitsAll : null;
  const blendedAnnualCost = totalCostAll * 12;
  const blendedHiddenFeesPct = totalCostAll > 0 ? (totalFeesAll / totalCostAll) * 100 : 0;

  return {
    blendedEffectiveYield,
    blendedAnnualCost,
    blendedCostPerUnit,
    blendedHiddenFeesPct,
    totalInterestAll,
    totalFeesAll,
    totalCostAll,
    totalUnitsAll,
  };
}

// ─── Recommendation Logic ─────────────────────────────────────────────────────

export interface Recommendation {
  priority: "high" | "medium" | "low";
  type: "fee" | "rate" | "turns" | "equity";
  message: string;
}

export function generateRecommendations(
  metrics: ComputedMetrics,
  breakdownSlices?: HiddenCostBreakdown,
): Recommendation[] {
  const recs: Recommendation[] = [];

  const bd = breakdownSlices ?? metrics.hiddenCostBreakdown;
  if (bd.feePctTotal > 50) {
    recs.push({
      priority: "high",
      type: "fee",
      message: `Fees account for ${bd.feePctTotal.toFixed(0)}% of your total floorplan cost. Focus negotiations on fee reductions for maximum savings.`,
    });
  } else if (bd.interestPct > 60) {
    recs.push({
      priority: "high",
      type: "rate",
      message: `Interest charges represent ${bd.interestPct.toFixed(0)}% of costs. Negotiating your rate will have the biggest impact.`,
    });
  }

  if (metrics.turnSensitivity) {
    const { plusOneTurnSavings, turnsCurrent } = metrics.turnSensitivity;
    if (plusOneTurnSavings > 1000) {
      recs.push({
        priority: "medium",
        type: "turns",
        message: `At ${turnsCurrent.toFixed(1)} turns/yr, improving by just 1 turn saves an estimated $${plusOneTurnSavings.toLocaleString("en-US", { maximumFractionDigits: 0 })}/yr.`,
      });
    }
  }

  if (metrics.capitalDrag?.flag === "destroying_equity") {
    recs.push({
      priority: "high",
      type: "equity",
      message: `Your floorplan yield (${metrics.capitalDrag.floorplanYield.toFixed(1)}%) exceeds your estimated inventory ROI (${metrics.capitalDrag.inventoryRoiAnnual.toFixed(1)}%). You may be destroying equity on some units.`,
    });
  }

  return recs;
}

// ─── Convenience: entry object → FloorplanInputs ─────────────────────────────

export function entryToInputs(
  entry: any,
  overrides?: Partial<FloorplanInputs>,
): FloorplanInputs {
  const n = (field: string) => parseFloat(entry[field] || "0");
  const i = (field: string) => parseInt(entry[field] || "0");

  const feeAdmin = n("floorplanFee");
  const feeAudit = n("auditFee");
  const feeCurtailment = n("curtailmentFee");
  const feeDoc = n("docFee");
  const feeTitle = n("titleFee");
  const feeAncillary1 = n("ancillaryFee1");
  const feeAncillary2 = n("ancillaryFee2");
  const feeAncillary3 = n("ancillaryFee3");
  const feeAncillary4 = n("ancillaryFee4");

  const base: FloorplanInputs = {
    totalInterestPaid: n("totalInterestPaid"),
    totalFeesPaid: n("totalFeesPaid"),
    avgBalance: n("avgInventoryBalance"),
    unitsSold: i("unitsSold"),
    avgDaysInStock: n("avgDaysOnFloor"),
    periodDays: i("periodDays") || 30,
    curtailmentPaidPrincipal: n("curtailmentPaidPrincipal"),
    unusedLineAmountAvg: n("unusedLineAmountAvg"),
    creditLimit: n("creditLimit"),
    grossProfitPerUnit: n("avgGrossProfitPerSale"),
    avgPurchasePrice: n("avgPurchasePrice"),
    dealerWacc: n("dealerWacc"),
    inventoryUnitsAvg: n("inventoryUnitsAvg"),
    statedRate: n("statedRate"),
    feeInterest: n("totalInterestPaid"),
    feeAdmin,
    feeAudit,
    feeCurtailment,
    feeDoc,
    feeTitle,
    feeOther: 0,
    feeAncillary1,
    feeAncillary2,
    feeAncillary3,
    feeAncillary4,
  };

  return { ...base, ...overrides };
}

// ─── Master compute function ──────────────────────────────────────────────────

export function computeAllMetrics(entry: any, overrides?: Partial<FloorplanInputs>): ComputedMetrics {
  const inp = entryToInputs(entry, overrides);
  const trueMonthlyCost = computeTrueMonthlyCost(inp);
  const effectiveYield = computeEffectiveYield(inp);
  const perUnitCost = computePerUnitCost(inp);
  const turnSensitivity = computeTurnSensitivity(inp);
  const hiddenCostBreakdown = computeHiddenCostBreakdown(inp);
  const curtailmentDrag = computeCurtailmentDrag(inp);
  const capitalDrag = computeCapitalDrag(inp);

  const projectedAnnualCost = trueMonthlyCost.value * 12;

  return {
    trueMonthlyCost,
    effectiveYield,
    perUnitCost,
    turnSensitivity,
    hiddenCostBreakdown,
    curtailmentDrag,
    capitalDrag,
    projectedAnnualCost,
  };
}
